import { FC, ReactElement } from 'react';
import classNames from 'classnames';
import { DialogPanel, DialogTitle } from '@headlessui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faXmark,
  faPersonRunning,
  faLayerGroup,
  faRectangleList,
  faSpinner,
} from '@fortawesome/pro-solid-svg-icons';
import { useModal } from 'xstate/hooks/useModal';
import { BoxDetailsModalPayload } from 'xstate/machines/ModalMachine';
import { closeModal } from 'xstate/actors/ModalActor';
import useBoxStats from 'networking/mindfeed/hooks/useBoxStats';
import type { BoxStats } from 'models/BoxStats';
import { useFormatBytes } from 'hooks/useFormatBytes';
import { useBoxLabels } from 'hooks/useBoxLabels';
import { MEMO_TYPE_ICON } from 'elements/Memo/memoTypeIcon';
import { MemoType } from 'models/Memo';
import { useTranslations } from 'next-intl';
import Button from 'elements/Button';

// mf-blue as an rgb triple so per-row tints can vary opacity like the mobile
// app's accent-colour ramp.
const ACCENT = '37, 87, 186';
const accent = (opacity: number) => `rgba(${ACCENT}, ${opacity})`;
const GREY = 'rgba(100, 116, 139, 0.25)'; // slate-500 @ 25%

// Ordered memo types with their accent-ramp opacity (kind → colour is fixed,
// independent of sort position). Icons and names are the ones every screen
// uses; Strava is a brand, greyed and last.
type MemoTypeMeta = {
  key: MemoType | 'strava';
  serverType: string;
  icon: IconProp;
  color: string;
};

const memoType = (type: MemoType, opacity: number): MemoTypeMeta => ({
  key: type,
  serverType: type,
  icon: MEMO_TYPE_ICON[type],
  color: accent(opacity),
});

const MEMO_TYPES: MemoTypeMeta[] = [
  memoType(MemoType.Text, 1),
  memoType(MemoType.Doc, 0.82),
  memoType(MemoType.Image, 0.66),
  memoType(MemoType.Link, 0.52),
  memoType(MemoType.Quote, 0.4),
  memoType(MemoType.Audio, 0.3),
  memoType(MemoType.Calendar, 0.22),
  memoType(MemoType.TaskList, 0.16),
  {
    key: 'strava',
    serverType: 'strava_activity',
    icon: faPersonRunning,
    color: GREY,
  },
];

const BoxDetailsModal = (): ReactElement => {
  const t = useTranslations();
  const { boxName } = useBoxLabels();
  const { payload } = useModal() as { payload: BoxDetailsModalPayload };
  const boxId = payload?.boxId;

  const { data: stats, isLoading } = useBoxStats({ boxId });

  return (
    <div className={styles.outer}>
      <div className={styles.inner}>
        <DialogPanel transition className={styles.panel}>
          <div className={styles.header.container}>
            <div className="min-w-0">
              <DialogTitle as="h2" className={styles.header.title}>
                {boxName(payload?.boxName)}
              </DialogTitle>
              <p className={styles.header.subtitle}>
                {t('boxDetails.subtitle')}
              </p>
            </div>
            <button
              type="button"
              onClick={closeModal}
              aria-label={t('common.close')}
              className={styles.header.close}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>

          <div className={styles.body}>
            <SummaryCards stats={stats} />

            {stats ? (
              <>
                <ByTypeCard memos={stats.memos} />
                <StorageCard media={stats.media} />
              </>
            ) : isLoading ? (
              <div className={styles.loading}>
                <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
              </div>
            ) : null}
          </div>

          <div className={styles.footer}>
            <Button
              type="button"
              variant="solid"
              color="primary"
              size="sm"
              onClick={closeModal}
              className="bg-mf-blue! hover:bg-mf-blue-600!"
            >
              {t('common.done')}
            </Button>
          </div>
        </DialogPanel>
      </div>
    </div>
  );
};

export default BoxDetailsModal;

// MARK: - Summary cards

const SummaryCards: FC<{ stats?: BoxStats }> = ({ stats }) => {
  const t = useTranslations();
  const usedTypes =
    stats?.memos.breakdown.filter((bucket) => bucket.count > 0).length ?? 0;
  const memosDetail = stats
    ? t('boxDetails.acrossTypes', { count: usedTypes })
    : '';

  const collectionsDetail = (() => {
    if (!stats) return '';
    const cols = stats.collections.summary.totalCount;
    if (cols === 0) return t('boxDetails.noCollections');
    const avg = Math.round(stats.memos.summary.totalCount / cols);
    return t('boxDetails.memosEach', { count: avg });
  })();

  return (
    <div className={styles.summary.grid}>
      <SummaryCard
        icon={faLayerGroup}
        label={t('boxDetails.memos')}
        count={stats?.memos.summary.totalCount}
        detail={memosDetail}
      />
      <SummaryCard
        icon={faRectangleList}
        label={t('boxDetails.collections')}
        count={stats?.collections.summary.totalCount}
        detail={collectionsDetail}
      />
    </div>
  );
};

const SummaryCard: FC<{
  icon: IconProp;
  label: string;
  count?: number;
  detail: string;
}> = ({ icon, label, count, detail }) => (
  <div className={styles.summary.card}>
    <div className={styles.summary.head}>
      <FontAwesomeIcon icon={icon} className={styles.summary.icon} />
      <span className={styles.summary.label}>{label}</span>
    </div>
    <p className={styles.summary.count}>{count != null ? count : '–'}</p>
    <p className={styles.summary.detail}>{detail}</p>
  </div>
);

// MARK: - By type card

const ByTypeCard: FC<{ memos: BoxStats['memos'] }> = ({ memos }) => {
  const t = useTranslations();
  const total = memos.summary.totalCount;
  const counts = new Map(memos.breakdown.map((b) => [b.type, b.count]));

  // Sort non-zero rows by descending count; keep zero rows in their base order.
  const rows = MEMO_TYPES.map((meta) => ({
    meta,
    count: counts.get(meta.serverType) ?? 0,
  }));
  const nonZero = rows
    .filter((r) => r.count > 0)
    .sort((a, b) => b.count - a.count);
  const zeros = rows.filter((r) => r.count === 0);
  const ordered = [...nonZero, ...zeros];

  return (
    <section className={styles.card.container}>
      <div className={styles.card.header}>
        <h3 className={styles.card.title}>{t('boxDetails.byType')}</h3>
        <span className={styles.card.meta}>
          {t('boxDetails.total', { count: total })}
        </span>
      </div>

      <SegmentedBar
        segments={ordered.map((r) => ({ value: r.count, color: r.meta.color }))}
      />

      <div>
        {ordered.map((row, index) => (
          <TypeRow
            key={row.meta.key}
            meta={row.meta}
            count={row.count}
            total={total}
            withDivider={index < ordered.length - 1}
          />
        ))}
      </div>
    </section>
  );
};

const TypeRow: FC<{
  meta: MemoTypeMeta;
  count: number;
  total: number;
  withDivider: boolean;
}> = ({ meta, count, total, withDivider }) => {
  const t = useTranslations();
  const isInactive = count === 0;
  const label = meta.key === 'strava' ? 'Strava' : t(`memoTypes.${meta.key}`);
  const percent =
    total > 0 && count > 0 ? Math.round((count / total) * 100) : null;

  return (
    <div
      className={classNames(
        styles.row.container,
        withDivider && styles.row.divider,
      )}
    >
      <div
        className={styles.row.tile}
        style={{
          backgroundColor: isInactive
            ? 'rgba(100, 116, 139, 0.12)'
            : accent(0.12),
        }}
      >
        <FontAwesomeIcon
          icon={meta.icon}
          style={{ color: isInactive ? '#64748b' : accent(1) }}
          className={styles.row.tileIcon}
        />
      </div>

      <div className="min-w-0 flex-1">
        <p
          className={classNames(
            styles.row.label,
            isInactive ? 'text-gray-400' : 'text-slate-800',
          )}
        >
          {label}
        </p>
        <p className={styles.row.sub}>
          {isInactive
            ? t('boxDetails.noneYet')
            : percent != null
              ? t('boxDetails.percentOfMemos', { percent })
              : ''}
        </p>
      </div>

      <span
        className={styles.row.count}
        style={{
          color: isInactive
            ? '#9ca3af'
            : meta.key === 'text'
              ? accent(1)
              : '#1e293b',
        }}
      >
        {count}
      </span>
    </div>
  );
};

// MARK: - Storage card

type StorageRow = { key: 'image' | 'audio' | 'video'; color: string };
const STORAGE_ROWS: StorageRow[] = [
  { key: 'image', color: accent(1) },
  { key: 'audio', color: accent(0.55) },
  { key: 'video', color: GREY },
];

const STORAGE_LABEL_KEYS = {
  image: 'boxDetails.media.images',
  audio: 'boxDetails.media.audio',
  video: 'boxDetails.media.video',
} as const;

const StorageCard: FC<{ media: BoxStats['media'] }> = ({ media }) => {
  const t = useTranslations();
  const formatBytes = useFormatBytes();
  const byType = new Map(media.breakdown.map((b) => [b.type, b]));
  const rows = STORAGE_ROWS.map((row) => ({
    ...row,
    bytes: byType.get(row.key)?.bytes ?? 0,
    count: byType.get(row.key)?.count ?? 0,
  }));

  const totalSize = formatBytes(media.summary.totalBytes);

  return (
    <section className={styles.card.container}>
      <div className={styles.card.header}>
        <h3 className={styles.card.title}>{t('boxDetails.storage')}</h3>
        <span className={styles.card.meta}>
          {t('boxDetails.fileCount', { count: media.summary.totalCount })}
        </span>
      </div>

      <p className={styles.storage.total}>
        <span className={styles.storage.totalValue}>{totalSize.value}</span>
        <span className={styles.storage.totalUnit}>{totalSize.unit}</span>
      </p>

      <SegmentedBar
        segments={rows.map((r) => ({ value: r.bytes, color: r.color }))}
      />

      <div className={styles.storage.rows}>
        {rows.map((row) => {
          const isInactive = row.count === 0;
          const size = formatBytes(row.bytes);
          return (
            <div key={row.key} className={styles.storage.row}>
              <span
                className={styles.storage.swatch}
                style={{ backgroundColor: row.color }}
              />
              <span
                className={classNames(
                  styles.storage.label,
                  isInactive ? 'text-gray-400' : 'text-slate-800',
                )}
              >
                {t(STORAGE_LABEL_KEYS[row.key])}
              </span>
              <span className={styles.storage.files}>
                {isInactive
                  ? '—'
                  : t('boxDetails.fileCount', { count: row.count })}
              </span>
              <span
                className={classNames(
                  styles.storage.size,
                  isInactive ? 'text-gray-400' : 'text-slate-800',
                )}
              >
                {size.value} {size.unit}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
};

// MARK: - Segmented bar

const SegmentedBar: FC<{ segments: { value: number; color: string }[] }> = ({
  segments,
}) => {
  const total = Math.max(
    segments.reduce((sum, s) => sum + Math.max(s.value, 0), 0),
    0.0001,
  );

  return (
    <div className={styles.bar.track}>
      {segments.map((segment, index) => (
        <div
          key={index}
          style={{
            width: `${(Math.max(segment.value, 0) / total) * 100}%`,
            backgroundColor: segment.color,
          }}
        />
      ))}
    </div>
  );
};

const styles = {
  outer: 'fixed inset-0 z-10 w-screen overflow-y-auto',
  inner: 'flex min-h-full items-center justify-center p-4',
  panel: classNames(
    'flex max-h-[85vh] w-full max-w-xl flex-col rounded-2xl bg-white text-left shadow-xl transition-all',
    'data-closed:opacity-0 data-closed:scale-95 data-enter:duration-200 data-leave:duration-150',
  ),
  header: {
    container: 'flex items-start justify-between gap-3 px-6 pt-6 pb-4',
    title: 'text-xl font-bold text-mf-off-black-950 truncate',
    subtitle: 'text-sm text-gray-500',
    close:
      'flex-none flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200',
  },
  body: 'flex-1 space-y-4 overflow-y-auto px-6 pb-2',
  loading: 'flex justify-center py-20 text-2xl text-gray-300',
  footer: 'flex justify-end px-6 py-4',
  summary: {
    grid: 'grid grid-cols-2 gap-3',
    card: 'rounded-2xl bg-gray-100/70 p-4',
    head: 'flex items-center gap-1.5 text-gray-500',
    icon: 'text-xs',
    label: 'text-xs font-semibold uppercase tracking-wide',
    count: 'mt-1 text-4xl font-bold leading-none text-mf-off-black-950',
    detail: 'mt-2 text-sm text-gray-500 truncate',
  },
  card: {
    container: 'space-y-3.5 rounded-2xl border border-gray-200 p-4',
    header: 'flex items-baseline justify-between',
    title: 'text-lg font-bold text-mf-off-black-950',
    meta: 'text-sm text-gray-500',
  },
  bar: {
    track: 'flex h-2 gap-0.5 overflow-hidden rounded-full bg-gray-100',
  },
  row: {
    container: 'flex items-center gap-3 py-2.5',
    divider: 'border-b border-gray-100',
    tile: 'flex-none flex items-center justify-center w-10 h-10 rounded-[10px]',
    tileIcon: 'text-sm',
    label: 'font-semibold truncate',
    sub: 'text-xs text-gray-500',
    count: 'text-lg font-bold',
  },
  storage: {
    total: 'flex items-baseline gap-1.5',
    totalValue: 'text-4xl font-bold leading-none text-mf-off-black-950',
    totalUnit: 'text-lg font-semibold text-gray-500',
    rows: 'space-y-2.5',
    row: 'flex items-center gap-3',
    swatch: 'flex-none w-3 h-3 rounded-[3px]',
    label: 'font-semibold',
    files: 'ml-auto text-sm text-gray-500',
    size: 'w-20 text-right text-sm font-semibold',
  },
};
